import Link from '@components/link';
import { useDeviceType } from '@hooks/use-device-type';
import type Icon from '@xo-union/tk-component-icons';
import classNames from 'classnames';
import React, {
	type ComponentProps,
	type MouseEventHandler,
	useCallback,
	useMemo,
} from 'react';
import onClickOutside from 'react-onclickoutside';
import type { Decorated } from 'types/vendor';
import { ShareModal } from './components/share_modal';
import {
	type UseShareModalType,
	useShareModal,
} from './components/share_modal/hooks/use-share-modal';
import SocialLinks from './components/social_links';
import Styles from './styles.scss';

interface Props {
	vendor: Decorated;
	icon?: ComponentProps<typeof Icon>['name'];
}

interface BottomAlertProps extends Pick<Props, 'vendor'> {
	closeModal: UseShareModalType['closeModal'];
}

const BottomAlert = onClickOutside(
	({ vendor, closeModal }: BottomAlertProps) => {
		const { claimedStatus } = vendor;
		return (
			<div data-testid="social_sharing_bottom_alert">
				{/* biome-ignore lint/a11y/useKeyWithClickEvents: Click only overlay */}
				<div
					className={classNames(
						Styles.alertOverlay,
						Styles.enhancementAlertOverlay,
					)}
					onClick={closeModal}
				/>
				<div
					className={classNames(
						claimedStatus === 'UNCLAIMED'
							? Styles.bottomAlertTaller
							: Styles.bottomAlert,
						Styles.enhancementAlertModal,
					)}
				>
					<SocialLinks vendor={vendor} />
				</div>
			</div>
		);
	},
);

interface SocialLinksOverlayProps extends Omit<Props, 'icon'> {
	closeModal: UseShareModalType['closeModal'];
	isModalOpen: UseShareModalType['isModalOpen'];
}

const SocialLinksOverlay = ({
	vendor,
	closeModal,
	isModalOpen,
}: SocialLinksOverlayProps) => {
	const { isMobile } = useDeviceType();

	if (isMobile) {
		return <BottomAlert vendor={vendor} closeModal={closeModal} />;
	}

	return <ShareModal modalIsOpen={isModalOpen} onClose={closeModal} />;
};

const SocialSharing = ({ vendor, icon }: Props) => {
	const { openModal, isModalOpen, closeModal } = useShareModal();
	const linkIcon = useMemo(() => {
		if (icon) {
			return { icon, iconSize: 'md' };
		}

		return { icon: 'share', iconSize: 'sm' };
	}, [icon]);

	const onClick: MouseEventHandler<HTMLAnchorElement> = useCallback(
		(e) => {
			e.preventDefault();
			openModal();
		},
		[openModal],
	);

	return (
		<>
			<span className={Styles.shareLinkContainer}>
				<Link
					websiteUrl="#"
					title="Share"
					{...linkIcon}
					onClick={onClick}
					iconClassName={Styles.shareLinkIcon}
					resetLinkStyle={true}
				/>
			</span>
			{isModalOpen && (
				<SocialLinksOverlay
					vendor={vendor}
					closeModal={closeModal}
					isModalOpen={isModalOpen}
				/>
			)}
		</>
	);
};

export default SocialSharing;
